import { parseLoginMethods, type LoginMethod } from '../utils/loginMethodCatalog';
import { createCampaignClient } from './campaign';
import { buildConversationLink } from './conversationLink';
import { acceptAuthTokenRenewal, getAuthSessionScope, invalidateAuthSessionScope } from './authSessionScope';
import { createSubscriptionClient } from './subscription';
import type { ModelCreditPricing } from '../utils/modelCreditPricing';
// NativeApp bridge: Flutter WebView registers a JavaScriptChannel named 'NativeApp'
declare global {
  interface Window {
    NativeApp?: { postMessage: (message: string) => void };
  }
}

import { CONFIG_KEYS, type AppConfig } from '../config';
import { configService } from './config';
import { getPlatformBaseUrl, onPlatformRequestFailed, onPlatformRequestSucceeded } from '../constants/auth';
import { localStore } from './store';
import { store } from '../store';
import { resetCoworkState } from '../store/slices/coworkSlice';
import { setAvailableModels, setSelectedModel } from '../store/slices/modelSlice';
import { clearRelayCache } from './relay';
import { fetchPlatformModelCatalog } from './platformModelCatalog';
import { createCreditClient } from './credit';
import type { AuthenticatedPlatformTransport } from './authenticatedPlatformTransport';
import { isPrimaryChatModel } from '../utils/modelCapabilityLabels';
import { modelSupportsImage, readModelCapabilities } from '../../shared/modelCapabilities';
import { mergeBoBotModelCatalogs } from './bobotModelCatalog';
import type {
  AuthState,
  AuthUser,
  Login2FAFormInput,
  LoginFormInput,
  PhoneLoginFormInput,
  RegisterFormInput,
  WeChatPhoneBindFormInput,
  WeChatSession,
  WeChatStatusResult,
} from '../types/auth';

type AuthListener = (state: AuthState | null) => void;

/** 平台不可达（网络错误 / 502+）时抛出；让登录页能给出针对性提示而非泛泛的"登录失败" */
export class PlatformUnreachableError extends Error {
  constructor(message = '平台服务暂时不可用，请稍后重试') {
    super(message);
    this.name = 'PlatformUnreachableError';
  }
}

/** 登录请求失败的 HTTP 语义，供扫码轮询区分限流、临时故障与终止错误。 */
export class AuthRequestError extends Error {
  constructor(message: string, readonly status: number, readonly retryAfterSeconds = 0, readonly code = '') {
    super(message);
    this.name = 'AuthRequestError';
  }
}

/** 兼容 Safari 15 的取消检查，不依赖较新的 AbortSignal.throwIfAborted。 */
function assertAuthRequestActive(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException('登录请求已取消', 'AbortError');
}

type AuthApiResult<T = unknown> = {
  success: boolean;
  message: string;
  data: T | null;
  status: number;
};

type LoginResult =
  | { status: 'authenticated'; auth: AuthState }
  | { status: 'requires_2fa'; username: string; password: string; ticket?: string | null };

type AuthTokenRecord = {
  key: string;
  name: string | null;
};

const normalizeBaseUrl = (value: string): string => value.trim().replace(/\/+$/, '');
const DEFAULT_BOCLAW_TOKEN_NAME = 'BoBot Default Key';
const AUTH_LOCAL_CACHE_KEY = 'boclaw_auth_state_cache';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getNestedValue = (source: unknown, path: string[]): unknown => {
  let current = source;
  for (const key of path) {
    if (!isRecord(current)) {
      return null;
    }
    current = current[key];
  }
  return current;
};

const getString = (source: unknown, paths: string[][]): string | null => {
  for (const path of paths) {
    const value = getNestedValue(source, path);
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }
  return null;
};

const getBoolean = (source: unknown, paths: string[][]): boolean | null => {
  for (const path of paths) {
    const value = getNestedValue(source, path);
    if (typeof value === 'boolean') {
      return value;
    }
  }
  return null;
};

const resolveMessage = (payload: unknown, fallback: string): string => (
  getString(payload, [
    ['message'],
    ['msg'],
    ['error'],
    ['data', 'message'],
    ['data', 'error'],
  ]) ?? fallback
);

const normalizeUser = (payload: unknown): AuthUser | null => {
  const username = getString(payload, [
    ['username'],
    ['name'],
    ['nickname'],
    ['email'],
    ['user', 'username'],
  ]);

  if (!username) {
    return null;
  }

  return {
    id: getString(payload, [['id'], ['userId'], ['uuid'], ['user', 'id']]) ?? username,
    username,
    displayName: getString(payload, [['display_name'], ['displayName'], ['user', 'display_name'], ['name']]),
    email: getString(payload, [['email'], ['user', 'email']]),
    phoneNumber: getString(payload, [['phone_number'], ['phoneNumber'], ['phone'], ['user', 'phone_number'], ['user', 'phone']]),
    group: getString(payload, [['group'], ['user', 'group']]),
    avatarUrl: getString(payload, [['avatar'], ['avatarUrl'], ['avatar_url'], ['user', 'avatar']]),
    role: getString(payload, [['role'], ['user', 'role']]),
    status: (() => {
      const value = getNestedValue(payload, ['status']);
      return typeof value === 'number' && Number.isFinite(value) ? value : null;
    })(),
  };
};

const SESSION_HEARTBEAT_INTERVAL_MS = 30 * 1000;

class AuthService {
  private authState: AuthState | null = null;
  private listeners = new Set<AuthListener>();
  private ensureBoBotApiKeyPromise: Promise<string> | null = null;
  private sessionHeartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private isHandlingSessionExpired = false;
  private renewalController: AbortController | null = null;
  private endingSession: AuthState | null = null;

  /** 积分、套餐与活动统一复用登录代理；身份只存在内存，不写入日志或持久缓存。 */
  private readonly platformTransport: AuthenticatedPlatformTransport = {
    identity: () => this.authState ? JSON.stringify([
      this.authState.user.id, this.authState.userIdHeader, this.authState.loggedInAt,
      getAuthSessionScope(), this.getBaseUrl(),
    ]) : null,
    request: async (path: string, isCurrentSession: () => boolean, body?: Record<string, unknown>) => this.request(path, {
      method: body ? 'POST' : 'GET',
      body,
      baseUrl: this.getBaseUrl(),
      headers: this.buildUserAuthHeaders({
        token: this.authState?.authMethod === 'bearer' ? this.authState.token : null,
        userId: this.authState?.userIdHeader,
      }),
      isCurrentSession,
    }),
  };
  readonly credits = createCreditClient(this.platformTransport);
  readonly subscriptions = createSubscriptionClient(this.platformTransport);
  readonly campaigns = createCampaignClient(this.platformTransport);

  subscribe(listener: AuthListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  getAuthState(): AuthState | null {
    return this.authState;
  }

  /** UI 与异步请求使用稳定的登录周期；正常续期不会改变，退出/换号立即改变。 */
  getSessionIdentity(): string | null {
    return this.authState ? getAuthSessionScope() : null;
  }

  getBaseUrl(): string {
    return normalizeBaseUrl(getPlatformBaseUrl());
  }

  async init(): Promise<AuthState | null> {
    type SavedState = AuthState & { newApiBaseUrl?: string; userIdHeader?: string; boclawApiKey?: string | null };

    // 优先从 localStorage 恢复（每台设备独立），避免多设备共享 server store 导致冲突
    let saved: SavedState | null = null;
    const currentToken = localStorage.getItem('boclaw_token');
    const hasLocalCache = !!localStorage.getItem(AUTH_LOCAL_CACHE_KEY);
    console.log('[auth] init state:', { hasToken: Boolean(currentToken), hasLocalCache });
    try {
      const cached = localStorage.getItem(AUTH_LOCAL_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached) as SavedState;
        if (parsed && currentToken && parsed.token === currentToken) {
          saved = parsed;
          console.info('[auth] Restored auth state from localStorage');
        } else {
          console.log('[auth] localStorage cache mismatch:', {
            cachedTokenPresent: Boolean(parsed?.token),
            currentTokenPresent: Boolean(currentToken),
          });
        }
      }
    } catch { /* parse failure */ }

    // localStorage 不可用时回退到 server store
    if (!saved) {
      const serverSaved = await localStore.getItem<SavedState | null>(CONFIG_KEYS.AUTH);
      console.log('[auth] server store auth:', serverSaved ? 'found' : 'empty', 'currentToken:', currentToken ? 'yes' : 'no');
      if (serverSaved) {
        if (!currentToken) {
          console.warn('[auth] No local token, clearing auth state');
          this.updateState(null);
          localStorage.removeItem(AUTH_LOCAL_CACHE_KEY);
          await localStore.removeItem(CONFIG_KEYS.AUTH);
          return null;
        }
        if (serverSaved.token !== currentToken) {
          // server store 被其他设备更新了，使用当前设备本地的 token
          console.info('[auth] Server auth_state differs (likely another device login), using local token');
          saved = { ...serverSaved, token: currentToken };
        } else {
          saved = serverSaved;
        }
      }
    }

    if (!saved) {
      this.updateState(null);
      return null;
    }

    // 使用标志位追踪 fetchSelf 期间是否被 handleUnauthorized 触发了自动退出
    let loggedOutByUnauthorized = false;
    const unsubscribe = this.subscribe((state) => {
      if (state === null) loggedOutByUnauthorized = true;
    });

    try {
      const authMethod = saved.authMethod ?? (saved.token ? 'bearer' : 'session');
      const userIdHeader = this.resolveUserIdHeader(saved.userIdHeader ?? saved.user?.id ?? null);
      if (!userIdHeader) {
        throw new Error('Missing New-Api-User header source');
      }

      // Try to refresh user profile from platform; fall back to cached data
      // if the platform token has expired or the platform is unreachable.
      let user: AuthUser;
      try {
        user = authMethod === 'bearer'
          ? await this.fetchSelf({ token: saved.token ?? undefined, userId: userIdHeader })
          : await this.fetchSelf({ userId: userIdHeader });
      } catch (fetchError) {
        // 如果 handleUnauthorized 已触发自动退出，不再用缓存恢复
        if (loggedOutByUnauthorized) {
          return null;
        }
        if (saved.user) {
          console.warn('[auth] Failed to refresh user profile, using cached state');
          user = saved.user;
        } else {
          throw fetchError;
        }
      }

      const nextState: AuthState = {
        token: saved.token ?? null,
        refreshToken: (saved as any).refreshToken ?? null,
        authMethod,
        user,
        userIdHeader,
        // Never revive model credentials from legacy localStorage/server KV.
        // fetchAndSaveBoBotModels obtains a fresh credential through the
        // authenticated Platform flow and keeps it only in live memory.
        boclawApiKey: null,
        loggedInAt: saved.loggedInAt ?? Date.now(),
      };
      await this.persist(nextState);
      this.updateState(nextState);
      try {
        await this.fetchAndSaveBoBotModels();
      } catch {
        console.error('[auth] Failed to fetch models from platform');
      }

      // Always ensure saved models from config are loaded into Redux
      this.loadSavedModelsToRedux();

      return nextState;
    } catch {
      console.warn('[auth] Failed to restore login state');
      await this.logout();
      return null;
    } finally {
      unsubscribe();
    }
  }

  async fetchCaptcha(): Promise<{ captchaId: string; captchaData: string } | null> {
    const baseUrl = this.getBaseUrl();
    if (!baseUrl) return null;

    try {
      const result = await this.request('/api/captcha', {
        method: 'GET',
        baseUrl,
      });

      if (result.success && result.data) {
        const data = result.data as Record<string, unknown>;
        const captchaId = getString(data, [['captcha_id'], ['captchaId']]);
        const captchaData = getString(data, [['captcha_data'], ['captchaData']]);
        if (captchaId && captchaData) {
          return { captchaId, captchaData };
        }
      }
    } catch {
      // Captcha not required
    }
    return null;
  }

  async login(input: LoginFormInput): Promise<LoginResult> {
    const username = input.username.trim();
    const password = input.password;
    const baseUrl = this.getBaseUrl();

    if (!baseUrl) {
      throw new Error('Platform base URL is not configured');
    }

    const body: Record<string, string> = { username, password, client_type: 'web' };
    if (input.captchaId && input.captchaCode) {
      body.captcha_id = input.captchaId;
      body.captcha_code = input.captchaCode;
    }

    // Try ce-web's platform-login proxy first — it forwards credentials to
    // the CE platform and issues a local JWT on success, solving the problem
    // that CE platform uses session/cookie auth and doesn't return a token.
    let result: AuthApiResult;
    try {
      const proxyResp = await fetch('/api/auth/platform-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const proxyJson = await proxyResp.json();
      const proxySuccess = proxyJson.success ?? proxyResp.ok;
      const proxyData = proxyJson.data ?? proxyJson;
      const proxyMessage = proxyJson.message ?? proxyJson.error ?? proxyResp.statusText ?? 'Request failed';
      result = { success: proxySuccess, data: proxyData, message: proxyMessage, status: proxyResp.status };
    } catch {
      // platform-login endpoint unavailable — fall back to direct platform call
      console.warn('[auth] platform-login proxy unavailable, falling back to direct platform call');
      try {
        result = await this.request('/api/user/login', {
          method: 'POST',
          baseUrl,
          body,
        });
      } catch {
        // 直连 Platform 也失败 → 真·平台不可达
        throw new PlatformUnreachableError();
      }
    }

    // sidecar 代理能回但返回 502/503（说明 sidecar 到 Platform 不通）；
    // 或者直连 Platform 返回 502+；都视为平台不可达
    if (result.status === 0 || result.status >= 502) {
      throw new PlatformUnreachableError();
    }

    const token = getString(result.data, [['token'], ['access_token'], ['data', 'token']]);
    const refreshToken = getString(result.data, [['refresh_token'], ['data', 'refresh_token']]);
    const userId = this.extractUserId(result.data);
    console.log('[auth] login result:', {
      success: result.success,
      hasToken: !!token,
      userId,
      dataKeys: result.data ? Object.keys(result.data as any) : [],
    });
    if (result.success && token) {
      const auth = await this.completeBearerLogin({
        token,
        refreshToken,
        userId,
        baseUrl,
        loginData: isRecord(result.data) ? result.data as Record<string, unknown> : undefined,
      });
      console.log('[auth] completeBearerLogin done, token saved:', !!localStorage.getItem('boclaw_token'));
      return { status: 'authenticated', auth };
    }

    if (result.success) {
      console.warn('[auth] login succeeded but NO token found, falling back to session login');
      const auth = await this.completeSessionLogin({
        userId,
        baseUrl,
      });
      return { status: 'authenticated', auth };
    }

    if (this.requires2FA(result.data, result.message)) {
      return {
        status: 'requires_2fa',
        username,
        password,
        ticket: getString(result.data, [['ticket'], ['data', 'ticket'], ['session'], ['data', 'session']]),
      };
    }

    throw new Error(result.message);
  }

  async loginWith2FA(input: Login2FAFormInput): Promise<AuthState> {
    const baseUrl = this.getBaseUrl();
    if (!baseUrl) {
      throw new Error('Platform base URL is not configured');
    }

    const payload: Record<string, string> = {
      username: input.username.trim(),
      password: input.password,
      code: input.code.trim(),
    };

    if (input.ticket?.trim()) {
      payload.ticket = input.ticket.trim();
    }

    let result;
    try {
      result = await this.request('/api/user/login/2fa', {
        method: 'POST',
        baseUrl,
        body: payload,
      });
    } catch {
      throw new PlatformUnreachableError();
    }

    if (result.status === 0 || result.status >= 502) {
      throw new PlatformUnreachableError();
    }

    const token = getString(result.data, [['token'], ['access_token'], ['data', 'token']]);
    const refreshToken = getString(result.data, [['refresh_token'], ['data', 'refresh_token']]);
    const userId = this.extractUserId(result.data);
    if (!result.success) {
      throw new Error(result.message);
    }

    if (token) {
      return this.completeBearerLogin({
        token,
        refreshToken,
        userId,
        baseUrl,
      });
    }

    return this.completeSessionLogin({
      userId,
      baseUrl,
    });
  }

  async register(input: RegisterFormInput): Promise<void> {
    const baseUrl = this.getBaseUrl();
    if (!baseUrl) {
      throw new Error('Platform base URL is not configured');
    }

    const result = await this.request('/api/user/register', {
      method: 'POST',
      baseUrl,
      body: {
        username: input.username.trim(),
        password: input.password,
        password2: input.password2,
        phone: input.phone.trim(),
        verification_code: input.verificationCode.trim(),
      },
    });

    if (!result.success) {
      throw new Error(result.message);
    }
  }

  async sendSmsCode(phone: string): Promise<void> {
    const value = phone.trim();
    if (!value) {
      throw new Error('Phone number is required');
    }
    await this.postLocalAuth('/api/auth/sms/send', { phone: value });
  }

  async sendSmsBindCode(phone: string): Promise<void> {
    const value = phone.trim();
    if (!value) {
      throw new Error('Phone number is required');
    }
    await this.postLocalAuth('/api/auth/sms/send-bind', { phone: value });
  }

  async loginWithPhone(input: PhoneLoginFormInput): Promise<AuthState> {
    const baseUrl = this.getBaseUrl();
    if (!baseUrl) {
      throw new Error('Platform base URL is not configured');
    }
    const result = await this.postLocalAuth('/api/auth/sms/login', {
      phone: input.phone.trim(),
      code: input.code.trim(),
    });
    return this.completeLocalLogin(result.data, baseUrl);
  }

  /** 为登录或注册发送短信，复用 Platform 已有的自动注册与限流能力。 */
  async sendLoginOrRegisterSms(phone: string): Promise<void> {
    await this.postLocalAuth('/api/auth/mobile/sms/send', { phone: phone.trim() });
  }

  /** 验证短信并登录；新用户由 Platform 按注册开关创建，不在 Web 中创建账号。 */
  async loginOrRegisterWithPhone(input: PhoneLoginFormInput): Promise<AuthState> {
    const baseUrl = this.getBaseUrl();
    if (!baseUrl) throw new Error('Platform base URL is not configured');
    const result = await this.postLocalAuth('/api/auth/mobile/sms/login', {
      phone: input.phone.trim(), code: input.code.trim(),
    });
    return this.completeLocalLogin(result.data, baseUrl);
  }

  /** 读取已配置 Platform 的登录能力，未知和管理员关闭均不开放入口。 */
  async getLoginCapabilities(): Promise<{ phone: boolean; wechat: boolean; appqr: boolean; methods?: LoginMethod[] }> {
    const { response, json } = await this.fetchLocalAuthPayload('/api/platform/health', { method: 'GET', cache: 'no-store' });
    const disabled = { phone: false, wechat: false, appqr: false };
    if (!response.ok || json?.success !== true || json?.data?.success === false) return disabled;
    const flags = json?.data?.data ?? json?.data;
    const methods = parseLoginMethods(flags?.login_methods);
    return { appqr: flags?.app_qr_login_enabled === true, phone: flags?.phone_verification_login === true, wechat: flags?.wechat_login === true, ...(methods ? { methods } : {}) };
  }

  /** 创建二维码；signal 随退出扫码或刷新二维码取消，过期时间由 Platform 决定。 */
  async createWeChatSession(signal?: AbortSignal): Promise<WeChatSession> {
    const result = await this.postLocalAuth('/api/auth/wechat/session', {}, signal);
    const sessionToken = getString(result.data, [['session_token'], ['sessionToken']]);
    const qrCodeUrl = getString(result.data, [['qr_code_url'], ['qrCodeUrl']]);
    const expiresInRaw = getNestedValue(result.data, ['expires_in']);
    const expiresIn = typeof expiresInRaw === 'number' ? expiresInRaw : 0;
    let validUrl = false;
    try {
      const url = new URL(qrCodeUrl ?? '');
      validUrl = url.protocol === 'https:' && !url.username && !url.password;
    } catch { /* 不渲染非 HTTP 图片或畸形地址。 */ }
    if (!sessionToken || !qrCodeUrl || !validUrl || !Number.isInteger(expiresIn) || expiresIn <= 0 || expiresIn > 3600) {
      throw new Error(result.message || 'WeChat session unavailable');
    }
    return { sessionToken, qrCodeUrl, expiresIn };
  }

  /** 查询扫码状态；取消或已有其他登录成功时，旧响应不得覆盖当前身份。 */
  async checkWeChatStatus(sessionToken: string, signal?: AbortSignal): Promise<WeChatStatusResult> {
    const baseUrl = this.getBaseUrl();
    if (!baseUrl) {
      throw new Error('Platform base URL is not configured');
    }
    const previousAuth = this.authState;
    const result = await this.postLocalAuth('/api/auth/wechat/status', { token: sessionToken }, signal);
    assertAuthRequestActive(signal);
    if (this.authState !== previousAuth || this.getBaseUrl() !== baseUrl) throw new DOMException('登录状态已变化', 'AbortError');
    const status = getString(result.data, [['status']]);
    if (status === 'authenticated') {
      const user = getNestedValue(result.data, ['user']) as Record<string, unknown> | null;
      if (!user) {
        throw new Error('WeChat login response missing user payload');
      }
      const auth = await this.completeLocalLogin(user, baseUrl);
      return { status: 'authenticated', auth };
    }
    if (status === 'require_phone_bind') {
      const wechatToken = getString(result.data, [['wechat_token'], ['wechatToken']]);
      if (!wechatToken) {
        throw new Error('Missing wechat_token in require_phone_bind response');
      }
      return { status: 'require_phone_bind', wechatToken };
    }
    if (status !== 'pending') throw new AuthRequestError('微信登录状态异常，请刷新二维码', 502);
    return { status: 'pending' };
  }

  /** 完成扫码后的手机号绑定；仅当前登录流程可以接收最终身份。 */
  async registerWeChatWithPhone(input: WeChatPhoneBindFormInput, signal?: AbortSignal): Promise<AuthState> {
    const baseUrl = this.getBaseUrl();
    if (!baseUrl) {
      throw new Error('Platform base URL is not configured');
    }
    const previousAuth = this.authState;
    const result = await this.postLocalAuth('/api/auth/wechat/register', {
      wechat_token: input.wechatToken,
      phone: input.phone.trim(),
      code: input.code.trim(),
    }, signal);
    assertAuthRequestActive(signal);
    if (this.authState !== previousAuth || this.getBaseUrl() !== baseUrl) throw new DOMException('登录状态已变化', 'AbortError');
    return this.completeLocalLogin(result.data, baseUrl);
  }

  /**
   * 登录 JSON 请求统一处理超时和取消；覆盖响应体读取，并在 finally 释放计时器和监听。
   * 使用基础 AbortController，兼容没有 timeout/any/throwIfAborted 的浏览器。
   */
  private async fetchLocalAuthPayload(
    path: string,
    init: RequestInit,
    signal?: AbortSignal,
  ): Promise<{ response: Response; json: any }> {
    assertAuthRequestActive(signal);
    const controller = new AbortController();
    const cancel = () => controller.abort();
    signal?.addEventListener('abort', cancel, { once: true });
    const timeout = setTimeout(cancel, 15000);
    try {
      const response = await fetch(path, { ...init, signal: controller.signal });
      assertAuthRequestActive(signal);
      let json: any;
      try { json = await response.json(); }
      catch {
        assertAuthRequestActive(signal);
        if (controller.signal.aborted) throw new AuthRequestError('登录请求超时，请重试', 0);
        throw new AuthRequestError(response.statusText || `Request failed (${response.status})`, response.status);
      }
      assertAuthRequestActive(signal);
      if (controller.signal.aborted) throw new AuthRequestError('登录请求超时，请重试', 0);
      return { response, json };
    } catch (error) {
      assertAuthRequestActive(signal);
      if (error instanceof AuthRequestError) throw error;
      throw new AuthRequestError(controller.signal.aborted ? '登录请求超时，请重试' : error instanceof Error ? error.message : 'Network error', 0);
    } finally {
      clearTimeout(timeout);
      signal?.removeEventListener('abort', cancel);
    }
  }

  /**
   * POST to a CE-web local /api/auth/* route (sidecar handles it). Throws on
   * non-success so callers can propagate error messages.
   */
  private async postLocalAuth(
    path: string,
    body: Record<string, unknown>,
    signal?: AbortSignal,
  ): Promise<AuthApiResult<Record<string, unknown>>> {
    const { response: resp, json } = await this.fetchLocalAuthPayload(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }, signal);
    const data: Record<string, unknown> | null = isRecord(json?.data)
      ? (json.data as Record<string, unknown>)
      : (isRecord(json) ? (json as Record<string, unknown>) : null);
    const message = resolveMessage(json, resp.statusText || `Request failed (${resp.status})`);
    const result: AuthApiResult<Record<string, unknown>> = {
      success: resp.ok && json?.success === true,
      message,
      data,
      status: resp.status,
    };
    if (!result.success) {
      const retry = Number(resp.headers.get('Retry-After'));
      throw new AuthRequestError(result.message, resp.status, Number.isFinite(retry) ? Math.min(60, Math.max(0, retry)) : 0, typeof json?.code === 'string' ? json.code : '');
    }
    return result;
  }

  /**
   * Complete login from a CE-web proxy response. The sidecar always packs the
   * local JWT + platform profile under `data.token` / `data.id` etc., matching
   * completeBearerLogin's expected shape.
   */
  private async completeLocalLogin(
    data: Record<string, unknown> | null,
    baseUrl: string,
  ): Promise<AuthState> {
    const token = getString(data, [['token'], ['access_token']]);
    if (!token) {
      throw new Error('Login succeeded but no token was issued');
    }
    const refreshToken = getString(data, [['refresh_token'], ['refreshToken']]);
    const userId = this.extractUserId(data);
    return this.completeBearerLogin({
      token,
      refreshToken,
      userId,
      baseUrl,
      loginData: data ?? undefined,
    });
  }

/** App 扫码请求只走同源 CE 接口，不把手机凭据交给浏览器。 */
  async appQrRequest(action: string, body: Record<string, unknown> = {}): Promise<Record<string, unknown>> {
    const result = await this.postLocalAuth('/api/auth/app-qr/' + action, body, AbortSignal.timeout(15_000));
    return result.data ?? {};
  }

  /** 复用正常 Web 登录的 JWT 存储、用户和模型初始化。 */
  async completeAppQrLogin(payload: Record<string, unknown>): Promise<void> {
    await this.completeLocalLogin(payload, this.getBaseUrl());
  }

  getBoBotApiKey(): string | null {
    const key = this.authState?.boclawApiKey;
    return typeof key === 'string' && key.trim() ? key.trim() : null;
  }

  async ensureBoBotApiKey(): Promise<string> {
    if (!this.authState) {
      throw new Error('BoBot account is not logged in');
    }

    const cachedKey = this.getBoBotApiKey();
    if (cachedKey) {
      return cachedKey;
    }

    if (this.ensureBoBotApiKeyPromise) {
      return this.ensureBoBotApiKeyPromise;
    }

    this.ensureBoBotApiKeyPromise = this._doEnsureBoBotApiKey().finally(() => {
      this.ensureBoBotApiKeyPromise = null;
    });

    return this.ensureBoBotApiKeyPromise;
  }

  private async _doEnsureBoBotApiKey(): Promise<string> {
    const baseUrl = this.getBaseUrl();
    if (!baseUrl) {
      throw new Error('Platform base URL is not configured');
    }

    const userIdHeader = this.resolveUserIdHeader();
    if (!userIdHeader) {
      throw new Error('Missing New-Api-User header source');
    }

    const headers = this.buildUserAuthHeaders({
      token: this.authState!.authMethod === 'bearer' ? this.authState!.token : null,
      userId: userIdHeader,
    });

    const existingTokens = await this.listApiTokens(baseUrl, headers);
    const existingKey = existingTokens.find((token) => token.name === DEFAULT_BOCLAW_TOKEN_NAME)?.key ?? null;
    if (existingKey) {
      await this.saveBoBotApiKey(existingKey);
      return existingKey;
    }

    const createdKey = await this.createDefaultApiToken(baseUrl, headers);
    await this.saveBoBotApiKey(createdKey);
    return createdKey;
  }

  async fetchSelf(options?: {
    token?: string | null;
    userId?: string | null;
    baseUrlOverride?: string;
  }): Promise<AuthUser> {
    const baseUrl = normalizeBaseUrl(options?.baseUrlOverride ?? this.getBaseUrl());
    const activeToken = options?.token ?? this.authState?.token ?? null;
    const userIdHeader = this.resolveUserIdHeader(options?.userId ?? null);

    if (!baseUrl) {
      throw new Error('Platform base URL is not configured');
    }

    if (!userIdHeader) {
      throw new Error('Missing New-Api-User header source');
    }

    const result = await this.request('/api/user/self', {
      method: 'GET',
      baseUrl,
      headers: this.buildUserAuthHeaders({
        token: activeToken,
        userId: userIdHeader,
      }),
    });

    if (!result.success || !result.data) {
      throw new Error(result.message);
    }

    const user = normalizeUser(result.data);
    if (!user) {
      throw new Error('Failed to parse user profile');
    }

    return user;
  }

  /**
   * 主动校验当前 token 是否仍然有效（调平台 /users/profile）。
   * token 无效时自动退出登录。
   */
  async verifyToken(): Promise<boolean> {
    if (!this.authState?.token) return false;
    try {
      await this.fetchSelf();
      return true;
    } catch {
      // fetchSelf → request() 内部已有 401 自动退出逻辑
      return false;
    }
  }

  async logout(): Promise<void> {
    const state = this.authState;
    const scope = getAuthSessionScope();
    this.endingSession = state;
    this.renewalController?.abort();
    this.stopSessionHeartbeat();
    const baseUrl = this.getBaseUrl();
    if (baseUrl) {
      try {
        const userIdHeader = this.resolveUserIdHeader();
        await this.request('/api/user/logout', {
          method: 'GET',
          baseUrl,
          headers: this.buildUserAuthHeaders({
            token: state?.authMethod === 'bearer' ? state.token : null,
            userId: userIdHeader,
          }),
          isCurrentSession: () => this.authState === state && getAuthSessionScope() === scope,
        });
      } catch {
        console.warn('[auth] Failed to log out remotely');
      }
    }
    if (this.endingSession === state) this.endingSession = null;
    // 远程登出期间可能已切换账号，不能清理新登录的凭证和配置。
    if (this.authState !== state || getAuthSessionScope() !== scope) return;
    // Clear this account's relay cache (desktop device names / session titles)
    // before wiping boclaw_user_id — otherwise a different account logging in
    // next on this same browser could briefly restore this account's cache.
    clearRelayCache();
    this.updateState(null);
    localStorage.removeItem(AUTH_LOCAL_CACHE_KEY);
    // 遗留键：早期版本把 refresh token 存在这里，现在 CE 不再使用该机制，
    // 保留这行是为了把老版本升级过来的用户数据一并清掉。
    localStorage.removeItem('boclaw_refresh_token');
    await localStore.removeItem(CONFIG_KEYS.AUTH);

    // Disable boclaw provider and clear its models to prevent stuck requests
    await this.clearBoBotProvider();
  }


  private async completeBearerLogin(input: {
    token: string;
    refreshToken?: string | null;
    userId: string | null;
    baseUrl: string;
    loginData?: Record<string, unknown>;
  }): Promise<AuthState> {
    const userIdHeader = this.resolveUserIdHeader(input.userId);
    if (!userIdHeader) {
      throw new Error('Login succeeded but user id is missing');
    }

    // Build user from login response data directly.
    // Do NOT call fetchSelf here — the token is a local JWT that platform
    // doesn't recognize, and a 401 from platform would trigger global logout
    // via handleUnauthorized, clearing localStorage before we can save the token.
    let user: AuthUser | null = input.loginData ? normalizeUser(input.loginData) : null;
    if (!user) {
      // Fallback: try fetchSelf (may work if platform accepts the token)
      user = await this.fetchSelf({
        token: input.token,
        userId: userIdHeader,
        baseUrlOverride: input.baseUrl,
      });
    }
    const auth: AuthState = {
      token: input.token,
      refreshToken: input.refreshToken ?? null,
      authMethod: 'bearer',
      user,
      userIdHeader,
      boclawApiKey: null,
      loggedInAt: Date.now(),
    };

    this.updateState(auth);
    await this.persist(auth);
    try {
      await this.fetchAndSaveBoBotModels();
    } catch {
      console.error('[auth] Failed to fetch models from platform');
    }
    return auth;
  }

  private async completeSessionLogin(input: {
    userId: string | null;
    baseUrl: string;
  }): Promise<AuthState> {
    const userIdHeader = this.resolveUserIdHeader(input.userId);
    if (!userIdHeader) {
      throw new Error('Login succeeded but user id is missing');
    }

    const user = await this.fetchSelf({
      userId: userIdHeader,
      baseUrlOverride: input.baseUrl,
    });
    const auth: AuthState = {
      token: null,
      refreshToken: null,
      authMethod: 'session',
      user,
      userIdHeader,
      boclawApiKey: null,
      loggedInAt: Date.now(),
    };

    this.updateState(auth);
    await this.persist(auth);
    try {
      await this.fetchAndSaveBoBotModels();
    } catch {
      console.error('[auth] Failed to fetch models from platform');
    }
    return auth;
  }

  private async persist(auth: AuthState): Promise<void> {
    // Model API keys are runtime credentials. Keep the live value only on the
    // in-memory authState; never copy it into localStorage or server KV.
    const durableAuth: AuthState = { ...auth, boclawApiKey: null };
    try {
      localStorage.setItem(AUTH_LOCAL_CACHE_KEY, JSON.stringify(durableAuth));
    } catch { /* localStorage full or unavailable; non-fatal */ }
    await localStore.setItem(CONFIG_KEYS.AUTH, durableAuth);
  }

  /** 更新并广播登录/资料状态；state 为 null 表示退出，后台凭证续期不走此入口。 */
  private updateState(state: AuthState | null) {
    const previous = this.authState;
    const identityChanged = previous?.user.id !== state?.user.id || previous?.token !== state?.token
      || previous?.loggedInAt !== state?.loggedInAt || previous?.authMethod !== state?.authMethod
      || previous?.userIdHeader !== state?.userIdHeader;
    if (identityChanged) {
      this.renewalController?.abort();
      invalidateAuthSessionScope();
      // 必须先停止旧订阅再清状态，避免清空动作被写入新账号的最近会话记录。
      window.dispatchEvent(new Event('auth:identityChanging'));
      if (previous) window.history.replaceState(null, '', buildConversationLink(window.location.href, null));
      clearRelayCache();
      store.dispatch(resetCoworkState());
    }
    this.authState = state;

    // Notify native app (Flutter WebView) of auth state changes
    try {
      if (typeof window.NativeApp !== 'undefined') {
        window.NativeApp.postMessage(JSON.stringify({
          type: state ? 'auth_success' : 'auth_logout',
        }));
      }
    } catch { /* ignore — only relevant inside Flutter WebView */ }

    // Persist token for web adapter to include in API requests
    if (state?.token) {
      localStorage.setItem('boclaw_token', state.token);
    } else {
      localStorage.removeItem('boclaw_token');
    }
    // CE 不再走 refresh token 路径：Platform 侧 session 由 token_version
    // + 滑动续期支撑，下面这行仅用于清掉历史遗留值，防止升级用户带着旧 key。
    localStorage.removeItem('boclaw_refresh_token');
    // Persist user identity for web adapter to include in API requests
    if (state?.user?.id) {
      localStorage.setItem('boclaw_user_id', state.user.id);
    } else {
      localStorage.removeItem('boclaw_user_id');
    }
    // Persist role for web adapter to include in API requests
    if (state?.user?.role) {
      localStorage.setItem('boclaw_user_role', state.user.role);
    } else {
      localStorage.removeItem('boclaw_user_role');
    }

    // Notify WebSocket layer to reconnect with updated credentials.
    // This ensures the WS connection carries the correct userId for event routing,
    // even when the initial WS connection was established before auth completed.
    window.dispatchEvent(new Event('auth:stateChanged'));

    this.listeners.forEach((listener) => listener(state));

    if (state?.user) {
      this.startSessionHeartbeat();
    } else {
      this.stopSessionHeartbeat();
    }
  }

  private startSessionHeartbeat(): void {
    if (this.sessionHeartbeatTimer) return;
    this.sessionHeartbeatTimer = setInterval(() => {
      // 401 由 request() 中的 handleSessionExpired 统一处理（logout + toast）
      this.fetchSelf().catch(() => { /* 非 401 静默忽略，平台离线有专门 banner */ });
      // 滑动续期：每次心跳顺便调 /api/auth/me 换新 JWT，超过 30 天空闲才失效
      this.renewTokenSilently().catch(() => { /* 续期失败静默，下次心跳再试 */ });
    }, SESSION_HEARTBEAT_INTERVAL_MS);
  }

  /// 调 sidecar /api/auth/me 做滑动续期。成功时用响应里的新 JWT 替换 authState.token + localStorage。
  /// 失败（401 / 平台不可达 / 网络错）全部静默，由 fetchSelf 心跳的常规路径负责报错。
  ///
  /// 注意：/api/auth/me 是 sidecar 自己的端点（不是 platform），必须打同源相对路径，
  /// **不能用 getBaseUrl()**（那是 platform URL，会被 proxy 转发到 platform 拿到 404）。
  private async renewTokenSilently(): Promise<void> {
    const state = this.authState;
    if (!state?.token || this.renewalController || this.endingSession === state) return;
    const scope = getAuthSessionScope();
    const baseUrl = this.getBaseUrl();
    const controller = new AbortController();
    this.renewalController = controller;
    const timeout = window.setTimeout(() => controller.abort(), 15_000);
    const isCurrent = () => !controller.signal.aborted && this.authState === state
      && this.endingSession !== state && this.getBaseUrl() === baseUrl && getAuthSessionScope() === scope;
    try {
      const resp = await fetch('/api/auth/me', {
        method: 'GET',
        signal: controller.signal,
        headers: {
          'Authorization': `Bearer ${state.token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!resp.ok) return; // 401 由 fetchSelf 那条心跳分支负责触发 logout
      const json = (await resp.json().catch(() => null)) as
        | { token?: string; data?: { renewed_token?: string } }
        | null;
      const renewed = json?.token || json?.data?.renewed_token || null;
      if (!isCurrent() || typeof renewed !== 'string' || !renewed.trim() || renewed === state.token) return;
      if (!acceptAuthTokenRenewal(scope, state.token, renewed)) return;
      const next: AuthState = { ...state, token: renewed };
      // 续期只替换凭证，不广播登录变化，避免清空积分、覆盖草稿或重复触发 App 登录回调。
      // 请求和断线重连会读取当前凭证；已有连接与页面继续沿用同一登录周期。
      this.authState = next;
      if (this.authState === next && getAuthSessionScope() === scope) await this.persist(next);
    } catch {
      // 网络错 / 同源被代理拦截等：静默，下次心跳重试
    } finally {
      window.clearTimeout(timeout);
      if (this.renewalController === controller) this.renewalController = null;
    }
  }

  private stopSessionHeartbeat(): void {
    if (this.sessionHeartbeatTimer) {
      clearInterval(this.sessionHeartbeatTimer);
      this.sessionHeartbeatTimer = null;
    }
  }

  /** 仅供 vite HMR dispose 用：清掉本实例的副作用（计时器、订阅），不清登录态。 */
  destroyForHmr(): void {
    this.renewalController?.abort();
    this.stopSessionHeartbeat();
    this.listeners.clear();
  }

  private async saveBoBotApiKey(apiKey: string): Promise<void> {
    if (!this.authState) {
      return;
    }

    const nextState: AuthState = {
      ...this.authState,
      boclawApiKey: apiKey,
    };
    await this.persist(nextState);
    this.updateState(nextState);
  }

  private async clearBoBotProvider(): Promise<void> {
    const config = configService.getConfig();
    const boclaw = config.providers?.boclaw;
    if (!boclaw?.enabled) return;

    await configService.updateConfig({
      providers: {
        ...config.providers,
        boclaw: { ...boclaw, enabled: false, models: [] },
      } as AppConfig['providers'],
    });

    // Rebuild Redux model list (excluding the now-disabled boclaw)
    const updatedConfig = configService.getConfig();
    const allModels: {
      id: string;
      name: string;
      provider?: string;
      supportsImage?: boolean;
      isImageGeneration?: boolean;
      capabilities?: string[];
      maxContext?: number;
      isPrimary?: boolean;
      endpointType?: number;
      creditPricing?: ModelCreditPricing;
    }[] = [];
    if (updatedConfig.providers) {
      Object.entries(updatedConfig.providers).forEach(([name, cfg]) => {
        if (cfg.enabled && cfg.models) {
          cfg.models.forEach((m: {
            id: string;
            name: string;
            supportsImage?: boolean;
            isImageGeneration?: boolean;
            capabilities?: string[];
            maxContext?: number;
            isPrimary?: boolean;
            endpointType?: number;
      creditPricing?: ModelCreditPricing;
          }) => {
            allModels.push({
              id: m.id,
              name: m.name,
              provider: name.charAt(0).toUpperCase() + name.slice(1),
              supportsImage: modelSupportsImage(m),
              isImageGeneration: m.isImageGeneration ?? false,
              capabilities: readModelCapabilities(m),
              ...(typeof m.maxContext === 'number' ? { maxContext: m.maxContext } : {}),
              ...(typeof m.isPrimary === 'boolean' ? { isPrimary: m.isPrimary } : {}),
              ...(typeof m.endpointType === 'number' ? { endpointType: m.endpointType } : {}),
              ...(m.creditPricing ? { creditPricing: m.creditPricing } : {}),
            });
          });
        }
      });
    }
    store.dispatch(setAvailableModels(allModels));
  }

  private async fetchAndSaveBoBotModels(): Promise<void> {
    const baseUrl = this.getBaseUrl();
    if (!baseUrl) {
      throw new Error('Platform base URL not configured');
    }

    const userIdHeader = this.resolveUserIdHeader();
    if (!userIdHeader) {
      throw new Error('User ID header not available');
    }

    // CE edition: platform IS the gateway, no separate gateway config needed.
    // Use the BoBot API key (created via /api/token/) as the gateway API key.
    const boclawApiKey = await this.ensureBoBotApiKey();
    const gatewayBaseUrl = baseUrl;

    // Save gateway_config to KV store (used by cowork runner)
    await localStore.setItem('gateway_config', {
      gateway_url: baseUrl,
      endpoint_path: '/v1',
      api_key: boclawApiKey,
    });

    // 平台主辅接口已拆分；保存全集供沙箱路由，选择器仍按 isPrimary 过滤。
    const rawModels = await fetchPlatformModelCatalog((path) => this.request(path, {
      method: 'GET',
      baseUrl,
      headers: { 'Authorization': `Bearer ${boclawApiKey}` },
    }));
    console.log('[auth] rawModels from CE platform:', rawModels.length);
    if (rawModels.length === 0) {
      console.warn('[auth] Platform returned empty model list');
      return;
    }

    // Parse OpenAI model format
    // BoCloudAI Platform 2026-05-03 起在 /v1/models 透传以下扩展字段:
    //   capabilities  — HuggingFace pipeline_tag csv (text-generation / image-text-to-text / 等)
    //   max_context   — 上下文长度
    //   is_primary    — 是否主对话模型
    //   endpoint_type — 1=对话,2=向量,3=重排,4=生图,5=TTS,6=STT,7=视频
    // 旧 platform 不下发这些字段时保留 undefined,UI 侧按 isPrimaryChatModel 兼容规则展示全部。
    const { models: fetchedModels } = mergeBoBotModelCatalogs(rawModels, []);

    if (fetchedModels.length === 0) {
      throw new Error('No valid models from platform');
    }

    // Save to provider config
    const config = configService.getConfig();
    // 辅助模型仅用于工具路由，不能成为默认会话模型；同步时保留有效选择。
    const primaryModels = fetchedModels.filter(isPrimaryChatModel);
    if (primaryModels.length === 0) {
      throw new Error('平台未返回可用的主对话模型，保留原配置');
    }
    const defaultModelId = primaryModels.some((model) => model.id === config.model.defaultModel)
      ? config.model.defaultModel
      : primaryModels[0].id;
    const currentBoclaw = config.providers?.boclaw ?? { enabled: false, apiKey: '', baseUrl: '', apiFormat: 'openai' as const, models: [] };
    const effectiveBaseUrl = gatewayBaseUrl || currentBoclaw.baseUrl || baseUrl;

    await configService.updateConfig({
      model: {
        ...config.model,
        ...(defaultModelId ? { defaultModel: defaultModelId } : {}),
      },
      providers: {
        ...config.providers,
        boclaw: {
          ...currentBoclaw,
          enabled: true,
          baseUrl: effectiveBaseUrl,
          apiKey: '',
          models: fetchedModels,
        },
      } as AppConfig['providers'],
    });

    // Sync updated models to Redux so dialogs reflect the change immediately
    const updatedConfig = configService.getConfig();
    const allModels: {
      id: string;
      name: string;
      provider?: string;
      supportsImage?: boolean;
      isImageGeneration?: boolean;
      capabilities?: string[];
      maxContext?: number;
      isPrimary?: boolean;
      endpointType?: number;
      creditPricing?: ModelCreditPricing;
    }[] = [];
    if (updatedConfig.providers) {
      Object.entries(updatedConfig.providers).forEach(([providerName, providerConfig]) => {
        if (providerConfig.enabled && providerConfig.models) {
          providerConfig.models.forEach((model: {
            id: string;
            name: string;
            supportsImage?: boolean;
            isImageGeneration?: boolean;
            capabilities?: string[];
            maxContext?: number;
            isPrimary?: boolean;
            endpointType?: number;
      creditPricing?: ModelCreditPricing;
          }) => {
            allModels.push({
              id: model.id,
              name: model.name,
              provider: providerName.charAt(0).toUpperCase() + providerName.slice(1),
              supportsImage: modelSupportsImage(model),
              isImageGeneration: model.isImageGeneration ?? false,
              capabilities: readModelCapabilities(model),
              ...(typeof model.maxContext === 'number' ? { maxContext: model.maxContext } : {}),
              ...(typeof model.isPrimary === 'boolean' ? { isPrimary: model.isPrimary } : {}),
              ...(typeof model.endpointType === 'number' ? { endpointType: model.endpointType } : {}),
              ...(model.creditPricing ? { creditPricing: model.creditPricing } : {}),
            });
          });
        }
      });
    }
    if (allModels.length > 0) {
      store.dispatch(setAvailableModels(allModels));
      // 如果平台设置了默认模型，优先选中
      if (defaultModelId) {
        const defaultModel = allModels.find((m) => m.id === defaultModelId);
        if (defaultModel) {
          store.dispatch(setSelectedModel(defaultModel));
        }
      }
    }
  }

  /**
   * Load models from saved app_config providers into Redux.
   * Called after init/login to ensure Redux has the latest model list,
   * even when fetchAndSaveBoBotModels() fails or returns empty.
   */
  loadSavedModelsToRedux(): void {
    const config = configService.getConfig();
    const allModels: {
      id: string;
      name: string;
      provider?: string;
      supportsImage?: boolean;
      isImageGeneration?: boolean;
      capabilities?: string[];
      maxContext?: number;
      isPrimary?: boolean;
      endpointType?: number;
      creditPricing?: ModelCreditPricing;
    }[] = [];
    if (config.providers) {
      Object.entries(config.providers).forEach(([providerName, providerConfig]) => {
        if (providerConfig.enabled && providerConfig.models) {
          providerConfig.models.forEach((model: {
            id: string;
            name: string;
            supportsImage?: boolean;
            isImageGeneration?: boolean;
            capabilities?: string[];
            maxContext?: number;
            isPrimary?: boolean;
            endpointType?: number;
      creditPricing?: ModelCreditPricing;
          }) => {
            allModels.push({
              id: model.id,
              name: model.name,
              provider: providerName.charAt(0).toUpperCase() + providerName.slice(1),
              supportsImage: modelSupportsImage(model),
              isImageGeneration: model.isImageGeneration ?? false,
              capabilities: readModelCapabilities(model),
              ...(typeof model.maxContext === 'number' ? { maxContext: model.maxContext } : {}),
              ...(typeof model.isPrimary === 'boolean' ? { isPrimary: model.isPrimary } : {}),
              ...(typeof model.endpointType === 'number' ? { endpointType: model.endpointType } : {}),
              ...(model.creditPricing ? { creditPricing: model.creditPricing } : {}),
            });
          });
        }
      });
    }
    store.dispatch(setAvailableModels(allModels));
    if (allModels.length > 0) {
      // 从配置中恢复默认模型，避免 setAvailableModels 回退到列表第一个
      const defaultModelId = config.model?.defaultModel;
      if (defaultModelId) {
        const defaultModel = allModels.find((m) => m.id === defaultModelId);
        if (defaultModel) {
          store.dispatch(setSelectedModel(defaultModel));
        }
      }
    }
  }

  private extractUserId(payload: unknown): string | null {
    return getString(payload, [
      ['id'],
      ['userId'],
      ['user', 'id'],
      ['data', 'id'],
      ['data', 'userId'],
    ]);
  }

  private resolveUserIdHeader(explicitUserId?: string | null): string | null {
    const candidate = explicitUserId
      ?? this.authState?.userIdHeader
      ?? this.authState?.user?.id
      ?? null;

    if (typeof candidate !== 'string' || !candidate.trim()) {
      return null;
    }

    return candidate.trim();
  }

  private buildUserAuthHeaders(input?: {
    token?: string | null;
    userId?: string | null;
  }): Record<string, string> {
    const headers: Record<string, string> = {};
    const token = input?.token ?? null;
    const userIdHeader = this.resolveUserIdHeader(input?.userId ?? null);

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (userIdHeader) {
      headers['New-Api-User'] = userIdHeader;
    }

    return headers;
  }

  private async listApiTokens(baseUrl: string, headers: Record<string, string>): Promise<AuthTokenRecord[]> {
    const result = await this.request('/api/token/?p=1&size=100', {
      method: 'GET',
      baseUrl,
      headers,
    });

    if (!result.success) {
      throw new Error(result.message);
    }

    return this.extractTokenItems(result.data)
      .map((item) => this.normalizeTokenRecord(item))
      .filter((item): item is AuthTokenRecord => item !== null);
  }

  private async createDefaultApiToken(baseUrl: string, headers: Record<string, string>): Promise<string> {
    const payloadCandidates: Array<Record<string, unknown>> = [
      {
        name: DEFAULT_BOCLAW_TOKEN_NAME,
        remain_quota: 500000,
        unlimited_quota: true,
        expired_time: -1,
        model_limits: [],
      },
      {
        name: DEFAULT_BOCLAW_TOKEN_NAME,
        remain_quota: 500000,
        unlimited_quota: true,
        expired_time: -1,
      },
      { name: DEFAULT_BOCLAW_TOKEN_NAME },
    ];

    let lastError: Error | null = null;

    for (const body of payloadCandidates) {
      try {
        const result = await this.request('/api/token/', {
          method: 'POST',
          baseUrl,
          headers,
          body,
        });

        if (!result.success) {
          lastError = new Error(result.message);
          continue;
        }

        // Token was created on the server — do NOT retry with another payload.
        const createdToken = this.extractTokenKey(result.data);
        if (createdToken) {
          return createdToken;
        }

        // Key not in response body; fetch token list to find it by name.
        const tokens = await this.listApiTokens(baseUrl, headers);
        const byName = tokens.find((t) => t.name === DEFAULT_BOCLAW_TOKEN_NAME)?.key ?? null;
        if (byName) {
          return byName;
        }

        throw new Error('Created API token but could not retrieve token key');
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Failed to create API token');
      }
    }

    throw lastError ?? new Error('Failed to create API token');
  }

  private extractTokenItems(payload: unknown): unknown[] {
    if (Array.isArray(payload)) {
      return payload;
    }
    if (!isRecord(payload)) {
      return [];
    }

    const candidates = [
      payload.data,
      payload.items,
      payload.list,
      payload.tokens,
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate;
      }
    }

    return [];
  }

  private normalizeTokenRecord(payload: unknown): AuthTokenRecord | null {
    const key = this.extractTokenKey(payload);
    if (!key) {
      return null;
    }

    return {
      key,
      name: getString(payload, [['name'], ['token_name']]),
    };
  }

  private extractTokenKey(payload: unknown): string | null {
    return getString(payload, [
      ['key'],
      ['token'],
      ['value'],
      ['access_token'],
      ['data', 'key'],
      ['data', 'token'],
      ['data', 'value'],
    ]);
  }

  private requires2FA(data: unknown, message: string): boolean {
    const explicit = getBoolean(data, [
      ['require2fa'],
      ['requires2fa'],
      ['two_factor_required'],
      ['data', 'require2fa'],
      ['data', 'requires2fa'],
      ['data', 'two_factor_required'],
    ]);

    if (explicit === true) {
      return true;
    }

    const combined = `${resolveMessage(data, '')} ${message}`.toLowerCase();
    return combined.includes('2fa') || combined.includes('two-factor') || combined.includes('two factor');
  }

  private async request(
    path: string,
    options: {
      method: string;
      baseUrl: string;
      headers?: Record<string, string>;
      body?: Record<string, unknown>;
      /** 可选的业务有效性校验；所有请求都会额外校验发起时的登录周期。 */
      isCurrentSession?: () => boolean;
    }
  ): Promise<AuthApiResult> {
    const requestScope = getAuthSessionScope();
    const requestToken = localStorage.getItem('boclaw_token');
    const isCurrent = () => getAuthSessionScope() === requestScope
      && (!options.isCurrentSession || options.isCurrentSession());
    try {
      const response = await window.electron.api.fetch({
        url: `${options.baseUrl}${path}`,
        method: options.method,
        headers: {
          'Content-Type': 'application/json',
          ...(options.headers ?? {}),
        },
        body: options.body ? JSON.stringify(options.body) : undefined,
      });

      if (!isCurrent()) {
        return { success: false, status: 409, message: 'Session changed', data: null };
      }
      const payload = response.data;
      const success = getBoolean(payload, [['success'], ['ok']]) ?? response.ok;
      const data = isRecord(payload) && 'data' in payload ? payload.data : payload;
      const message = resolveMessage(payload, response.statusText || 'Request failed');

      if (response.ok) {
        onPlatformRequestSucceeded();
      } else if (response.status === 0 || response.status >= 502) {
        onPlatformRequestFailed();
      }

      // 平台会话失效（token_version 不匹配、cookie 过期或被管理员踢下线），
      // 统一在此登出并提示，避免用户继续停留在"看起来已登录"的界面上。
      // Platform 两种返回方式都要识别：
      //   1. HTTP 401：cookie 有效但 tv 不匹配（管理员踢人/改密码的典型场景）
      //   2. HTTP 200 + success:false + 消息含"登录/未登录/access token 无效"：
      //      sidecar 侧 cookie 丢失走 access token 分支又失败时 Platform 的约定返回。
      // 以下路径不参与「登录态失效」判定：
      //   /api/user/login|register —— 登录本身失败是业务错误，不是会话过期
      //   /v1/*                    —— OpenAI 兼容网关，用的是独立的网关 API Key
      //                               （见 fetchModels 里显式带的 Authorization），
      //                               它的 401 只代表网关 key 无效 / 该账号没有模型权限，
      //                               误判成会话过期会让用户刚登录成功就被立刻踢出去。
      const skipSessionExpiryCheck = path.startsWith('/api/user/login')
        || path.startsWith('/api/user/register')
        || path.startsWith('/v1/');
      if (!skipSessionExpiryCheck) {
        const looksLikeAuthFail =
          response.status === 401 ||
          (success === false &&
            typeof message === 'string' &&
            // 只认"登录/token"相关失败，不要误伤"权限不足"
            /登录已失效|未登录|access token 无效|New-Api-User 与登录用户不匹配/.test(message));
        // 旧凭证的失败不能推翻已经完成的续期；下一次心跳使用新凭证核验。
        if (looksLikeAuthFail && requestToken === localStorage.getItem('boclaw_token')) {
          console.warn('[auth] Platform authentication rejected', {
            routeClass: path.startsWith('/api/') ? 'api' : 'other',
            status: response.status,
          });
          if (this.authState?.user) {
            this.handleSessionExpired();
          } else if (localStorage.getItem('boclaw_token')) {
            // During app bootstrap authState has not been restored yet. Waiting
            // for handleSessionExpired() would do nothing and leave the app on
            // its loading screen with a permanently invalid cached token.
            this.clearInvalidStartupAuth();
          }
        }
      }

      return {
        success,
        message,
        data,
        status: response.status,
      };
    } catch (err) {
      if (isCurrent()) onPlatformRequestFailed();
      throw err;
    }
  }

  private clearInvalidStartupAuth(): void {
    clearRelayCache();
    this.updateState(null);
    localStorage.removeItem(AUTH_LOCAL_CACHE_KEY);
    localStorage.removeItem('boclaw_refresh_token');
    void localStore.removeItem(CONFIG_KEYS.AUTH).catch(() => {
      console.warn('[auth] Failed to remove invalid startup auth state');
    });
  }

  private handleSessionExpired(): void {
    if (this.isHandlingSessionExpired) return;
    if (!this.authState?.user) return;
    this.isHandlingSessionExpired = true;
    // 立即派发提示（不等待 logout 完成），并用持久模式，让用户清楚看到原因
    window.dispatchEvent(
      new CustomEvent('app:showToast', {
        detail: {
          message: '登录状态已失效，可能由于账号密码变更或被管理员下线，请重新登录',
          persistent: true,
        },
      }),
    );
    void (async () => {
      try {
        await this.logout();
      } catch {
        console.warn('[auth] Failed to logout on session expired');
      } finally {
        this.isHandlingSessionExpired = false;
      }
    })();
  }
}

export const authService = new AuthService();

// vite HMR dispose hook: 模块热更新时清掉本实例的 setInterval/listeners，
// 避免老模块的心跳计时器残留导致一个浏览器里同时跑多套心跳（出现过 platform
// 收到旧代码发的 /api/auth/me 404，因为新代码已改为同源 fetch 但老 setInterval 还在跑）。
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    try {
      authService.destroyForHmr();
    } catch {
      // ignore — 仅 best-effort 清理
    }
  });
}
